/**
 * 🏢 Gestión de Centros de Costo y Asignaciones
 *
 * Permite:
 * - Ver y gestionar catálogo de centros de costo
 * - Asignar empleados a centros con porcentajes
 * - Ver asignaciones actuales por empleado
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Grid,
  Autocomplete,
  TableSortLabel,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Business,
  Assignment,
  Save,
  Cancel
} from '@mui/icons-material';
import { API_BASE_URL } from '../../config';

const GestionCentrosCosto = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Estado para centros de costo
  const [centros, setCentros] = useState([]);
  const [dialogCentroOpen, setDialogCentroOpen] = useState(false);
  const [centroEdit, setCentroEdit] = useState(null);
  const [formCentro, setFormCentro] = useState({
    codigo: '',
    nombre: '',
    descripcion: '',
    activo: true
  });

  // Estado para asignaciones
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState(null);
  const [asignaciones, setAsignaciones] = useState([]);
  const [dialogAsignacionOpen, setDialogAsignacionOpen] = useState(false);
  const [asignacionEdit, setAsignacionEdit] = useState(null);
  const [formAsignacion, setFormAsignacion] = useState({
    centro_costo_id: '',
    porcentaje: 100,
    fecha_desde: new Date().toISOString().split('T')[0],
    fecha_hasta: '',
    observaciones: ''
  });

  // Búsqueda de empleados
  const [empleadosBusqueda, setEmpleadosBusqueda] = useState([]);
  const [busquedaEmpleado, setBusquedaEmpleado] = useState('');
  const [todosEmpleados, setTodosEmpleados] = useState([]);
  const [ordenEmpleados, setOrdenEmpleados] = useState({ campo: 'nombre_completo', dir: 'asc' });
  const [filtroCentro, setFiltroCentro] = useState('');
  const [centrosActivos, setCentrosActivos] = useState([]);

  useEffect(() => {
    cargarCentros();
    cargarCentrosActivos();
    cargarTodosEmpleados();
  }, []);

  useEffect(() => {
    if (empleadoSeleccionado) {
      cargarAsignacionesEmpleado(empleadoSeleccionado.legajo);
    }
  }, [empleadoSeleccionado]);

  useEffect(() => {
    if (busquedaEmpleado && busquedaEmpleado.length >= 2) {
      buscarEmpleados(busquedaEmpleado);
    }
  }, [busquedaEmpleado]);

  const cargarCentros = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/centros-costo`, {
        withCredentials: true
      });
      setCentros(response.data);
    } catch (error) {
      console.error('Error al cargar centros:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al cargar centros'
      });
    } finally {
      setLoading(false);
    }
  };

  const cargarCentrosActivos = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/centros-costo/activos`, {
        withCredentials: true
      });
      setCentrosActivos(response.data);
    } catch (error) {
      console.error('Error al cargar centros activos:', error);
    }
  };

  const buscarEmpleados = async (query) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/centros-costo/empleados/buscar`, {
        params: { query },
        withCredentials: true
      });
      setEmpleadosBusqueda(response.data);
    } catch (error) {
      console.error('Error al buscar empleados:', error);
    }
  };

  const cargarTodosEmpleados = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/centros-costo/empleados`, {
        withCredentials: true
      });
      setTodosEmpleados(response.data);
    } catch (error) {
      console.error('Error al cargar empleados:', error);
    }
  };

  const cargarAsignacionesEmpleado = async (legajo) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/centros-costo/asignaciones/${legajo}`, {
        withCredentials: true
      });
      setAsignaciones(response.data);
    } catch (error) {
      console.error('Error al cargar asignaciones:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al cargar asignaciones'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialogCentro = (centro = null) => {
    if (centro) {
      setCentroEdit(centro);
      setFormCentro({
        codigo: centro.codigo,
        nombre: centro.nombre,
        descripcion: centro.descripcion || '',
        activo: centro.activo
      });
    } else {
      setCentroEdit(null);
      setFormCentro({
        codigo: '',
        nombre: '',
        descripcion: '',
        activo: true
      });
    }
    setDialogCentroOpen(true);
  };

  const handleOpenDialogAsignacion = (asignacion = null) => {
    if (!empleadoSeleccionado) {
      setMessage({ type: 'error', text: 'Primero seleccione un empleado' });
      return;
    }

    if (asignacion) {
      setAsignacionEdit(asignacion);
      setFormAsignacion({
        centro_costo_id: asignacion.centro_id,
        porcentaje: asignacion.porcentaje,
        fecha_desde: asignacion.fecha_desde ? asignacion.fecha_desde.split('T')[0] : '',
        fecha_hasta: asignacion.fecha_hasta ? asignacion.fecha_hasta.split('T')[0] : '',
        observaciones: asignacion.observaciones || ''
      });
    } else {
      setAsignacionEdit(null);
      setFormAsignacion({
        centro_costo_id: '',
        porcentaje: 100,
        fecha_desde: new Date().toISOString().split('T')[0],
        fecha_hasta: '',
        observaciones: ''
      });
    }
    setDialogAsignacionOpen(true);
  };

  const handleGuardarCentro = async () => {
    try {
      setLoading(true);

      if (centroEdit) {
        // Actualizar
        await axios.put(`${API_BASE_URL}/api/centros-costo/${centroEdit.id}`, formCentro, {
          withCredentials: true
        });
        setMessage({ type: 'success', text: 'Centro actualizado correctamente' });
      } else {
        // Crear
        await axios.post(`${API_BASE_URL}/api/centros-costo`, formCentro, {
          withCredentials: true
        });
        setMessage({ type: 'success', text: 'Centro creado correctamente' });
      }

      setDialogCentroOpen(false);
      cargarCentros();
      cargarCentrosActivos();

    } catch (error) {
      console.error('Error al guardar centro:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al guardar centro'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGuardarAsignacion = async () => {
    try {
      setLoading(true);

      const datos = {
        ...formAsignacion,
        legajo: empleadoSeleccionado.legajo
      };

      if (asignacionEdit) {
        // Actualizar
        await axios.put(`${API_BASE_URL}/api/centros-costo/asignaciones/${asignacionEdit.id}`, formAsignacion, {
          withCredentials: true
        });
        setMessage({ type: 'success', text: 'Asignación actualizada correctamente' });
      } else {
        // Crear
        await axios.post(`${API_BASE_URL}/api/centros-costo/asignaciones`, datos, {
          withCredentials: true
        });
        setMessage({ type: 'success', text: 'Asignación creada correctamente' });
      }

      setDialogAsignacionOpen(false);
      cargarAsignacionesEmpleado(empleadoSeleccionado.legajo);

    } catch (error) {
      console.error('Error al guardar asignación:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al guardar asignación'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEliminarAsignacion = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta asignación?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/centros-costo/asignaciones/${id}`, {
        withCredentials: true
      });

      setMessage({ type: 'success', text: 'Asignación eliminada correctamente' });
      cargarAsignacionesEmpleado(empleadoSeleccionado.legajo);

    } catch (error) {
      console.error('Error al eliminar asignación:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al eliminar asignación'
      });
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return 'Indefinido';
    return new Date(fecha).toLocaleDateString('es-AR');
  };

  return (
    <Box>
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label="Centros de Costo" icon={<Business />} iconPosition="start" />
        <Tab label="Asignaciones de Empleados" icon={<Assignment />} iconPosition="start" />
      </Tabs>

      {/* Tab 1: Centros de Costo */}
      {tabValue === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Catálogo de Centros de Costo</Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialogCentro()}
              >
                Nuevo Centro
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Código</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Descripción</TableCell>
                      <TableCell align="center">Estado</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {centros.map((centro) => (
                      <TableRow key={centro.id}>
                        <TableCell>{centro.codigo}</TableCell>
                        <TableCell><strong>{centro.nombre}</strong></TableCell>
                        <TableCell>{centro.descripcion || '-'}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={centro.activo ? 'Activo' : 'Inactivo'}
                            color={centro.activo ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDialogCentro(centro)}
                            title="Editar"
                          >
                            <Edit />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab 2: Asignaciones */}
      {tabValue === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Asignar Empleados a Centros de Costo
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                sx={{ flex: 2 }}
                label="Buscar empleado (legajo o nombre)"
                placeholder="Escriba para filtrar..."
                value={busquedaEmpleado}
                onChange={(e) => setBusquedaEmpleado(e.target.value)}
              />
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Filtrar por centro</InputLabel>
                <Select
                  value={filtroCentro}
                  label="Filtrar por centro"
                  onChange={(e) => setFiltroCentro(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {centrosActivos.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {!empleadoSeleccionado && (
              <TableContainer component={Paper} sx={{ mb: 3, maxHeight: 350 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={ordenEmpleados.campo === 'legajo' ? ordenEmpleados.dir : false}>
                        <TableSortLabel
                          active={ordenEmpleados.campo === 'legajo'}
                          direction={ordenEmpleados.campo === 'legajo' ? ordenEmpleados.dir : 'asc'}
                          onClick={() => setOrdenEmpleados(o => o.campo === 'legajo' ? { campo: 'legajo', dir: o.dir === 'asc' ? 'desc' : 'asc' } : { campo: 'legajo', dir: 'asc' })}
                        >Legajo</TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={ordenEmpleados.campo === 'nombre_completo' ? ordenEmpleados.dir : false}>
                        <TableSortLabel
                          active={ordenEmpleados.campo === 'nombre_completo'}
                          direction={ordenEmpleados.campo === 'nombre_completo' ? ordenEmpleados.dir : 'asc'}
                          onClick={() => setOrdenEmpleados(o => o.campo === 'nombre_completo' ? { campo: 'nombre_completo', dir: o.dir === 'asc' ? 'desc' : 'asc' } : { campo: 'nombre_completo', dir: 'asc' })}
                        >Nombre</TableSortLabel>
                      </TableCell>
                      <TableCell align="center">Acción</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {todosEmpleados
                      .filter(e =>
                        (!busquedaEmpleado ||
                          (e.legajo && e.legajo.toString().includes(busquedaEmpleado)) ||
                          (e.nombre_completo && e.nombre_completo.toLowerCase().includes(busquedaEmpleado.toLowerCase()))) &&
                        (!filtroCentro || e.centro_ids.includes(Number(filtroCentro)))
                      )
                      .sort((a, b) => {
                        const va = ordenEmpleados.campo === 'legajo' ? Number(a.legajo) : a.nombre_completo.toLowerCase();
                        const vb = ordenEmpleados.campo === 'legajo' ? Number(b.legajo) : b.nombre_completo.toLowerCase();
                        if (va < vb) return ordenEmpleados.dir === 'asc' ? -1 : 1;
                        if (va > vb) return ordenEmpleados.dir === 'asc' ? 1 : -1;
                        return 0;
                      })
                      .map((emp) => (
                        <TableRow
                          key={emp.legajo}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => setEmpleadoSeleccionado(emp)}
                        >
                          <TableCell>{emp.legajo}</TableCell>
                          <TableCell>{emp.nombre_completo}</TableCell>
                          <TableCell align="center">
                            <Button size="small" variant="outlined" onClick={() => setEmpleadoSeleccionado(emp)}>
                              Ver asignaciones
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    }
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {empleadoSeleccionado && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button size="small" variant="text" onClick={() => { setEmpleadoSeleccionado(null); setBusquedaEmpleado(''); }}>
                      ← Volver al listado
                    </Button>
                    <Typography variant="subtitle1">
                      Asignaciones de: <strong>{empleadoSeleccionado.nombre_completo}</strong> (Legajo: {empleadoSeleccionado.legajo})
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenDialogAsignacion()}
                  >
                    Nueva Asignación
                  </Button>
                </Box>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : asignaciones.length === 0 ? (
                  <Alert severity="info">Este empleado no tiene asignaciones configuradas</Alert>
                ) : (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Centro de Costo</TableCell>
                          <TableCell align="center">Porcentaje</TableCell>
                          <TableCell>Fecha Desde</TableCell>
                          <TableCell>Fecha Hasta</TableCell>
                          <TableCell>Observaciones</TableCell>
                          <TableCell align="center">Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {asignaciones.map((asig) => (
                          <TableRow key={asig.id}>
                            <TableCell><strong>{asig.centro_nombre}</strong></TableCell>
                            <TableCell align="center">
                              <Chip label={`${asig.porcentaje}%`} color="primary" size="small" />
                            </TableCell>
                            <TableCell>{formatearFecha(asig.fecha_desde)}</TableCell>
                            <TableCell>{formatearFecha(asig.fecha_hasta)}</TableCell>
                            <TableCell>{asig.observaciones || '-'}</TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenDialogAsignacion(asig)}
                                title="Editar"
                              >
                                <Edit />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleEliminarAsignacion(asig.id)}
                                title="Eliminar"
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialog para crear/editar centro */}
      <Dialog
        open={dialogCentroOpen}
        onClose={() => setDialogCentroOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {centroEdit ? 'Editar Centro de Costo' : 'Nuevo Centro de Costo'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Código *"
                value={formCentro.codigo}
                onChange={(e) => setFormCentro({ ...formCentro, codigo: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre *"
                value={formCentro.nombre}
                onChange={(e) => setFormCentro({ ...formCentro, nombre: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Descripción"
                value={formCentro.descripcion}
                onChange={(e) => setFormCentro({ ...formCentro, descripcion: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formCentro.activo}
                    onChange={(e) => setFormCentro({ ...formCentro, activo: e.target.checked })}
                  />
                }
                label="Activo"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogCentroOpen(false)} startIcon={<Cancel />}>
            Cancelar
          </Button>
          <Button
            onClick={handleGuardarCentro}
            variant="contained"
            startIcon={<Save />}
            disabled={!formCentro.codigo || !formCentro.nombre}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para crear/editar asignación */}
      <Dialog
        open={dialogAsignacionOpen}
        onClose={() => setDialogAsignacionOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', py: 1.5 }}>
          {asignacionEdit ? 'Editar Asignación' : 'Nueva Asignación'}
        </DialogTitle>
        <DialogContent sx={{ pt: 4, pb: 2, px: 3 }}>
          {/* Fila 1: Centro + Porcentaje */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, mt: 1 }}>
            <Box sx={{ flex: 3 }}>
              <Autocomplete
                options={centrosActivos}
                getOptionLabel={(option) => option.nombre}
                value={centrosActivos.find(c => c.id === formAsignacion.centro_costo_id) || null}
                onChange={(event, newValue) => {
                  setFormAsignacion({ ...formAsignacion, centro_costo_id: newValue?.id || '' });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Centro de Costo *" size="small" />
                )}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Porcentaje *"
                value={formAsignacion.porcentaje}
                onChange={(e) => setFormAsignacion({ ...formAsignacion, porcentaje: parseFloat(e.target.value) })}
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                helperText="0 - 100"
              />
            </Box>
          </Box>

          {/* Fila 2: Fecha Desde + Fecha Hasta */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              sx={{ flex: 1 }}
              size="small"
              type="date"
              label="Fecha Desde *"
              value={formAsignacion.fecha_desde}
              onChange={(e) => setFormAsignacion({ ...formAsignacion, fecha_desde: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              sx={{ flex: 1 }}
              size="small"
              type="date"
              label="Fecha Hasta"
              value={formAsignacion.fecha_hasta}
              onChange={(e) => setFormAsignacion({ ...formAsignacion, fecha_hasta: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Vacío = indefinido"
            />
          </Box>

          {/* Fila 3: Observaciones */}
          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            label="Observaciones"
            value={formAsignacion.observaciones}
            onChange={(e) => setFormAsignacion({ ...formAsignacion, observaciones: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogAsignacionOpen(false)} startIcon={<Cancel />} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleGuardarAsignacion}
            variant="contained"
            startIcon={<Save />}
            disabled={!formAsignacion.centro_costo_id || !formAsignacion.porcentaje || !formAsignacion.fecha_desde}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GestionCentrosCosto;
